import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * The landing's section label: a Chile Ancho hairline and a tracked uppercase
 * eyebrow in the same colour.
 *
 * This used to be a deliberately quiet label, on the argument that an eyebrow
 * repeated over every section stops being a system and becomes grammar nobody
 * chose. That argument lost to the register: in the brand register colour leads,
 * and the ember appearing once per section is the rhythm that makes the page
 * read as Menny rather than as a well-set template. The restraint it protected
 * still holds everywhere it matters — the eyebrow is still banned outright in
 * admin, staff and the diner surface.
 *
 * `onBrandSurface` is the Comal panel, and it is deliberately not called `dark`:
 * the two are different facts. Comal is a fixed composition that does not flip
 * with the theme, so its foreground has to be `--brand-surface-foreground`. The
 * old `dark` branch resolved through `--background`, which is itself near-black
 * in dark mode — the label would have gone invisible on the panel.
 */
export function SectionRule({
  children,
  onBrandSurface = false,
}: {
  children: ReactNode
  onBrandSurface?: boolean
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "h-px w-6",
          onBrandSurface ? "bg-brand-surface-foreground/45" : "bg-brand"
        )}
      />
      <span
        className={cn(
          "text-xs font-semibold uppercase tracking-[0.18em]",
          onBrandSurface ? "text-brand-surface-foreground/70" : "text-brand-ink"
        )}
      >
        {children}
      </span>
    </div>
  )
}

/**
 * One capability as a ruled row rather than a card. The dot carries real
 * information — whether the feature is on the moment a location is created, or
 * whether the restaurant switches it on — which is the distinction
 * `config/location_features.yml` draws with `default_enabled`.
 */
export function CapabilityRow({
  title,
  description,
  shipped,
  last = false,
}: {
  title: string
  description: string
  shipped: boolean
  last?: boolean
}) {
  return (
    <div className={cn("grid grid-cols-[1rem_minmax(0,1fr)] gap-3 border-t py-3.5", last && "border-b")}>
      <span
        className={cn(
          "mt-1.5 size-2 rounded-full",
          shipped ? "bg-brand" : "border border-brand/50"
        )}
        aria-hidden
      />
      <span className="flex flex-col gap-1">
        <span className="text-base font-semibold tracking-tight">{title}</span>
        <span className="text-sm leading-6 text-muted-foreground">{description}</span>
      </span>
    </div>
  )
}

export function LandingSection({
  id,
  className,
  children,
}: {
  id?: string
  className?: string
  children: ReactNode
}) {
  return (
    <section id={id} className={cn("mx-auto w-full max-w-7xl px-4 pb-20 lg:pb-24", className)}>
      {children}
    </section>
  )
}

/**
 * Every section headline on the landing. `rotulo` carries the width, weight,
 * line-height and tracking — this is the brand register, and the width axis is
 * the whole reason the family was chosen. Size stays here because the rótulo has
 * no ceiling and each section sets its own.
 */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={cn("rotulo text-balance text-3xl sm:text-4xl lg:text-5xl", className)}>
      {children}
    </h2>
  )
}
